import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { authApi } from '../api/services';
import { PLAN_LIMIT_EVENT, type PlanLimitDetail, PRINCIPAL_CHANGED_EVENT } from '../api/client';
import {
  entitlementKey,
  entitlementsApi,
  type AiUsageSummary,
  type EntitlementKey,
  type TenantEntitlements,
  type TenantSeats,
} from '../api/entitlements';

// ---------------------------------------------------------------------------
// What the signed-in tenant's plan includes. Loaded once per sign-in from
// GET /api/tenant/entitlements, then refreshed when:
//   - the server says the tenant changed (SignalR TenantUpdated, re-broadcast
//     by AppLayout as the `cadence:tenant-updated` window event),
//   - the window regains focus and the copy is more than 5 minutes old,
//   - a request is refused for an entitlement we thought the plan had (a
//     downgrade elsewhere), or for the AI allowance (usage moved on).
//
// "Unknown" never locks anything: until the server has answered, `has()` is
// true, so the nav and the pages look exactly as they did before plans
// existed. The last answer is kept per user in localStorage so a reload
// doesn't flash leadership items in and then out again. If the endpoint
// fails we fall back to the `features` block on /auth/me (the old source for
// projects / transcripts / webhooks / PDF / SSO), so nothing regresses.
//
// The backend enforces every one of these; this is display only.
// ---------------------------------------------------------------------------

export const TENANT_UPDATED_EVENT = 'cadence:tenant-updated';

const STORAGE_KEY = 'cadence_entitlements_v1';
const STALE_AFTER_MS = 5 * 60_000;
const UPGRADE_REFRESH_MIN_MS = 30_000;

export type EntitlementsStatus = 'unknown' | 'ready' | 'fallback';

export interface EntitlementsValue {
  /** 'unknown' = not loaded (or not a tenant user); 'fallback' = only /auth/me features. */
  status: EntitlementsStatus;
  data: TenantEntitlements | null;
  /** Show-by-default: false only when the server has said the plan lacks it. */
  has: (entitlement: EntitlementKey) => boolean;
  /** Strict: undefined while unknown. */
  known: (entitlement: EntitlementKey) => boolean | undefined;
  /** The cheapest plan that includes it, when the server has said. */
  requiredPlan: (entitlement: EntitlementKey) => string | undefined;
  plan: string | null;
  isTrial: boolean;
  isSelfServeIndividual: boolean;
  seats: TenantSeats | null;
  aiUsage: AiUsageSummary | null;
  refresh: () => Promise<void>;
}

// /auth/me `features` → entitlement keys, for the fallback path.
const ME_FEATURE_KEYS: Array<[string, EntitlementKey]> = [
  ['projectsEnabled', 'projects'],
  ['transcriptProcessingEnabled', 'transcriptProcessing'],
  ['webhooksEnabled', 'webhooks'],
  ['pdfExportEnabled', 'pdfExport'],
  ['ssoEnabled', 'sso'],
];

function buildValue(
  status: EntitlementsStatus,
  data: TenantEntitlements | null,
  fallback: Partial<Record<EntitlementKey, boolean>> | null,
  refresh: () => Promise<void>,
): EntitlementsValue {
  const known = (e: EntitlementKey): boolean | undefined => {
    if (status === 'ready' && data) return data.entitlements?.[e];
    if (status === 'fallback' && fallback) return fallback[e];
    return undefined;
  };
  return {
    status,
    data,
    has: (e) => known(e) !== false,
    known,
    requiredPlan: (e) => data?.requiredPlans?.[e] ?? undefined,
    plan: data?.plan ?? null,
    isTrial: !!data?.isTrial,
    isSelfServeIndividual: !!data?.isSelfServeIndividual,
    seats: data?.seats ?? null,
    aiUsage: data?.aiUsage ?? null,
    refresh,
  };
}

const noopRefresh = async () => { /* no provider */ };

/** What a component sees with no provider above it: nothing is locked. */
export const UNKNOWN_ENTITLEMENTS: EntitlementsValue = buildValue('unknown', null, null, noopRefresh);

const EntitlementsContext = createContext<EntitlementsValue | null>(null);

/**
 * The tenant's plan entitlements. Outside a provider (a page rendered on its
 * own in a test) it answers "unknown", which locks nothing.
 */
export function useEntitlements(): EntitlementsValue {
  return useContext(EntitlementsContext) ?? UNKNOWN_ENTITLEMENTS;
}

interface Cached { userId: string; data: TenantEntitlements }

function readCache(userId: string): TenantEntitlements | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Cached;
    return parsed?.userId === userId && parsed.data?.entitlements ? parsed.data : null;
  } catch {
    return null;
  }
}

function writeCache(userId: string, data: TenantEntitlements) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ userId, data } as Cached)); } catch { /* storage unavailable */ }
}

export function EntitlementsProvider({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated } = useAuth();
  const userId = isAuthenticated && user && user.role !== 'PlatformAdmin' ? user.id : null;
  const tenantId = user?.tenantId ?? null;

  const [data, setData] = useState<TenantEntitlements | null>(() => (userId ? readCache(userId) : null));
  const [fallback, setFallback] = useState<Partial<Record<EntitlementKey, boolean>> | null>(null);
  const [status, setStatus] = useState<EntitlementsStatus>(() => (data ? 'ready' : 'unknown'));

  const fetchedAtRef = useRef(0);
  const requestRef = useRef(0);
  const userIdRef = useRef(userId);
  const dataRef = useRef(data);
  useEffect(() => { userIdRef.current = userId; dataRef.current = data; });

  const load = useCallback(async () => {
    const forUser = userIdRef.current;
    if (!forUser) return;
    const request = ++requestRef.current;
    fetchedAtRef.current = Date.now();
    try {
      const next = await entitlementsApi.get();
      if (request !== requestRef.current || forUser !== userIdRef.current) return;
      setData(next);
      setStatus('ready');
      writeCache(forUser, next);
    } catch {
      if (request !== requestRef.current || forUser !== userIdRef.current) return;
      try {
        const me: any = await authApi.me();
        if (request !== requestRef.current || forUser !== userIdRef.current) return;
        const features = me?.features;
        if (features && typeof features === 'object') {
          const mapped: Partial<Record<EntitlementKey, boolean>> = {};
          for (const [field, key] of ME_FEATURE_KEYS) {
            if (typeof features[field] === 'boolean') mapped[key] = features[field];
          }
          setFallback(mapped);
          // A previous full answer (cached or fetched) beats the thinner fallback.
          setStatus(prev => (prev === 'ready' ? prev : 'fallback'));
        }
      } catch { /* stay unknown: nothing gets locked */ }
    }
  }, []);

  // (Re)load for each signed-in user; clear on sign-out or user switch.
  useEffect(() => {
    requestRef.current++;
    fetchedAtRef.current = 0;
    setFallback(null);
    if (!userId) {
      setData(null);
      setStatus('unknown');
      return;
    }
    const cached = readCache(userId);
    setData(cached);
    setStatus(cached ? 'ready' : 'unknown');
    void load();
  }, [userId, load]);

  // Plan changed on the server (checkout, App Store, platform admin).
  useEffect(() => {
    if (!userId) return;
    const onTenantUpdated = (e: Event) => {
      const changed = (e as CustomEvent<{ tenantId?: string }>).detail?.tenantId;
      if (changed && tenantId && changed.toLowerCase() !== tenantId.toLowerCase()) return;
      void load();
    };
    window.addEventListener(TENANT_UPDATED_EVENT, onTenantUpdated);
    return () => window.removeEventListener(TENANT_UPDATED_EVENT, onTenantUpdated);
  }, [userId, tenantId, load]);

  // An EA switched executive: the AI usage shown is that executive's.
  useEffect(() => {
    if (!userId) return;
    const onPrincipalChanged = () => { void load(); };
    window.addEventListener(PRINCIPAL_CHANGED_EVENT, onPrincipalChanged);
    return () => window.removeEventListener(PRINCIPAL_CHANGED_EVENT, onPrincipalChanged);
  }, [userId, load]);

  // Back to the tab after a while (the plan may have changed in another tab
  // or on another device while the socket was asleep).
  useEffect(() => {
    if (!userId) return;
    const onFocus = () => {
      if (document.visibilityState === 'hidden') return;
      if (Date.now() - fetchedAtRef.current > STALE_AFTER_MS) void load();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [userId, load]);

  // The server refused something our copy says the plan has: the copy is
  // stale (a downgrade that happened elsewhere). A refusal we already expected
  // (a locked feature) changes nothing. Throttled either way.
  useEffect(() => {
    if (!userId) return;
    const onPlanLimit = (e: Event) => {
      const detail = (e as CustomEvent<PlanLimitDetail>).detail;
      const key = entitlementKey(detail?.entitlement);
      if (key && dataRef.current?.entitlements?.[key] === false) return;
      if (Date.now() - fetchedAtRef.current > UPGRADE_REFRESH_MIN_MS) void load();
    };
    window.addEventListener(PLAN_LIMIT_EVENT, onPlanLimit);
    return () => window.removeEventListener(PLAN_LIMIT_EVENT, onPlanLimit);
  }, [userId, load]);

  const value = useMemo(
    () => (userId ? buildValue(status, data, fallback, load) : UNKNOWN_ENTITLEMENTS),
    [userId, status, data, fallback, load],
  );

  return <EntitlementsContext.Provider value={value}>{children}</EntitlementsContext.Provider>;
}

/** A fixed value, for tests and previews. */
export function StaticEntitlementsProvider({
  value,
  refresh = noopRefresh,
  children,
}: {
  value: TenantEntitlements | null;
  /** Called where the live provider would re-read the entitlements. */
  refresh?: () => Promise<void>;
  children: React.ReactNode;
}) {
  const ctx = useMemo(
    () => (value ? buildValue('ready', value, null, refresh) : UNKNOWN_ENTITLEMENTS),
    [value, refresh],
  );
  return <EntitlementsContext.Provider value={ctx}>{children}</EntitlementsContext.Provider>;
}
